import {createContext, useContext, useEffect} from 'react';

import {isDevelopment} from '../../../../core/src/helpers/environment/envHelpers';
import {Unsubscriber} from '../../../../core/src/helpers/react/reactHelpers';

/*
 * Types.
 */

export interface CursorPosition {
  cursorX: number;
  cursorY: number;
}

/*
 * Props.
 */

export interface MouseCursorContextProps {
  cursorPosition: CursorPosition | null;
  subscribe: () => Unsubscriber;
}

/*
 * Context.
 */

const MouseCursorContext = createContext<MouseCursorContextProps | undefined>(undefined);

export const MouseCursorContextProvider = MouseCursorContext.Provider;

/*
 * Hook.
 */

export function useMouseCursorContext(shouldSubscribe: boolean) {
  const context = useContext(MouseCursorContext);

  if (isDevelopment() && shouldSubscribe && !context) {
    throw new Error('Missing MouseCursorContext. The cursor position will not be tracked.');
  }

  // Extract stable subscribe function to avoid triggering the effect too often.
  const subscribe = context?.subscribe;

  useEffect(() => {
    if (!shouldSubscribe) {
      return undefined;
    }

    if (subscribe === undefined) {
      return undefined;
    }

    return subscribe();
  }, [subscribe, shouldSubscribe]);

  return context?.cursorPosition;
}
